"use client";

import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { ptBR } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FALTA_DIA_ANTERIOR,
  FALTA_DIA_SEGUINTE,
  SOBRA_DIA_ANTERIOR,
  SOBRA_DIA_SEGUINTE,
} from "@/constants/justifications.id";

export function JustiticationDatePicker({
  row,
  setValue,
  justificationId,
  conciliationDate,
  isClosed,
}: {
  row: any;
  setValue: any;
  justificationId?: number;
  conciliationDate: string;
  isClosed: boolean;
}) {
  const previousDayJustifications = [FALTA_DIA_ANTERIOR, SOBRA_DIA_ANTERIOR];
  const nextDayJustifications = [FALTA_DIA_SEGUINTE, SOBRA_DIA_SEGUINTE];
  const dateBasedJustifications = [
    ...previousDayJustifications,
    ...nextDayJustifications,
  ];

  const isDateBasedJustification = justificationId
    ? dateBasedJustifications.includes(justificationId)
    : false;
  const isPreviousDayJustification = justificationId
    ? previousDayJustifications.includes(justificationId)
    : false;
  const isNextDayJustification = justificationId
    ? nextDayJustifications.includes(justificationId)
    : false;

  const getDisabledDate = (date: Date) => {
    const referenceDate = new Date(conciliationDate);
    referenceDate.setHours(0, 0, 0, 0);

    if (isPreviousDayJustification) {
      return date > referenceDate;
    }

    if (isNextDayJustification) {
      return date < referenceDate;
    }

    return false;
  };

  const [date, setDate] = useState<Date | undefined>(() => {
    if (row.original.justification_date) {
      return new Date(row.original.justification_date);
    }

    if (isDateBasedJustification) {
      return new Date(conciliationDate);
    }

    return undefined;
  });

  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    if (newDate) {
      setValue(
        `divergence.justification_date_${row.original.id}`,
        newDate.toISOString()
      );
    }
  };

  return (
    <div className="w-full flex items-center justify-start p-0 border-none">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            disabled={isClosed}
            className={cn(
              "w-[200px] justify-start text-left text-[14px] font-normal border-none",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-3 w-3" />
            {date ? (
              format(date, "dd/MM/yyyy", { locale: ptBR })
            ) : (
              <span>Selecione uma data</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 border-none">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            defaultMonth={
              isDateBasedJustification ? new Date(conciliationDate) : undefined
            }
            disabled={getDisabledDate ?? isClosed}
            initialFocus
            locale={ptBR}
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
